import { useState } from 'react';

import { AppIcon, AppIcons, InfoModal, UploadedFile } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import {
  StoryDocument,
  useUpdateUserProfileMutation,
} from '@backrs_ui/grow-ui/src/redux/profile/profile';

import {
  Box,
  Button,
  MenuItem,
  Select,
  Stack,
  SxProps,
  TextField,
  Theme,
  Typography,
} from '@mui/material';

import { FileUploadConfig } from '../types/profile.types';

const MODAL_DOCUMENT_STRINGS = appStrings.modules.profile.modalUploader.document;
const MAX_CHAR_LIMIT = 50;

type ProfileDocumentUploadModalProps = Readonly<{
  open: boolean;
  mediaId: number;
  file: File;

  modalTitle: string;
  modalDescription: string;
  mediaType: 'document' | 'video';
  fileUploadConfig: FileUploadConfig;

  icon: AppIcons;

  onClose: () => void;
  onSuccess?: () => void;
  onError?: (error: Error) => void;
  userId: number;
}>;

export const ProfileDocumentUploadModal = ({
  open,
  mediaId,
  file,
  modalTitle,
  modalDescription,
  mediaType,
  fileUploadConfig,
  icon,
  onClose,
  onSuccess,
  onError,
  userId,
}: ProfileDocumentUploadModalProps) => {
  const [updateUserProfile] = useUpdateUserProfileMutation();

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [visibility, setVisibility] = useState<'public' | 'panel'>(
    fileUploadConfig.visibility ?? 'public',
  );

  const [questionId, setQuestionId] = useState(
    fileUploadConfig.dropDownOptions ? undefined : fileUploadConfig.questionId,
  );

  const handleSubmit = async () => {
    try {
      if (!userId) throw new Error('Missing user id');

      const getMediaType = () => {
        const fileExtension = file.name.split('.').pop() || '';

        if (['mp4', 'mov'].includes(fileExtension)) {
          return 'video';
        }

        if (['png', 'jpg', 'jpeg', 'gif'].includes(fileExtension)) {
          return 'image';
        }

        if (
          ['pdf', 'doc', 'docx', 'txt', 'csv', 'xls', 'xlsx', 'ppt', 'pptx'].includes(fileExtension)
        ) {
          return 'document';
        }

        return mediaType;
      };

      const profileData: Omit<StoryDocument, 'id' | 'media_url' | 'profile_option_answers_id'> = {
        user_id: userId,
        category_id: fileUploadConfig.categoryId,
        question_id: questionId || '',
        media_type: getMediaType(),
        title,
        description,
        visibility,
        option_id: fileUploadConfig.optionId as
          | 'resume'
          | 'transcript'
          | 'other'
          | 'personal_assessment'
          | null,
        media_id: mediaId,
      };

      await updateUserProfile({
        field: fileUploadConfig.key,
        value: profileData,
      }).unwrap();

      onSuccess?.();
      onClose();
    } catch (error) {
      console.error(error);
      onError?.(error as Error);
    }
  };

  return (
    <InfoModal
      headerContent={
        <Box>
          <Typography variant="text-lg-semibold">{modalTitle}</Typography>
          <Typography variant="text-sm-regular" sx={modalDescriptionSx}>
            {modalDescription}
          </Typography>

          <UploadedFile
            fileName={file.name.length > 60 ? file.name.slice(0, 60) + '...' : file.name}
            fileSize={
              file.size > 1024 * 1024
                ? `${(file.size / 1024 / 1024).toFixed(2)} MB`
                : `${(file.size / 1024).toFixed(2)} KB`
            }
            fileType={file.name.split('.').pop() || ''}
            handleDelete={onClose}
          />
        </Box>
      }
      open={open}
      width="sm"
      onClose={onClose}
      icon={icon}
      dialogSx={dialogSx}
      bodyContent={
        <Stack gap={2}>
          {/**
           * TITLE AND DESCRIPTION
           */}
          <Box>
            <Typography variant="text-sm-medium" sx={formLabelSx}>
              {MODAL_DOCUMENT_STRINGS.modal.title}
            </Typography>
            <TextField
              value={title}
              onChange={(e) => setTitle(e.target.value.slice(0, MAX_CHAR_LIMIT))}
              fullWidth
              helperText={
                title.length >= MAX_CHAR_LIMIT - 10
                  ? `${title.length}/${MAX_CHAR_LIMIT}`
                  : undefined
              }
              inputProps={{ maxLength: MAX_CHAR_LIMIT }}
            />
          </Box>

          <Box>
            <Typography variant="text-sm-medium" sx={formLabelSx}>
              {MODAL_DOCUMENT_STRINGS.modal.description}
            </Typography>
            <TextField
              value={description}
              onChange={(e) => setDescription(e.target.value.slice(0, MAX_CHAR_LIMIT))}
              multiline
              rows={2}
              fullWidth
              helperText={
                description.length >= MAX_CHAR_LIMIT - 10
                  ? `${description.length}/${MAX_CHAR_LIMIT}`
                  : undefined
              }
              inputProps={{ maxLength: MAX_CHAR_LIMIT }}
            />
          </Box>

          {/**
           * DROPDOWN FIELDS
           */}
          {fileUploadConfig.dropDownOptions &&
            fileUploadConfig.dropDownOptions.length > 0 &&
            fileUploadConfig.dropDownOptions?.map((dropDownCategory) => (
              <Box key={dropDownCategory.title}>
                <Typography variant="text-sm-medium" sx={formLabelSx}>
                  {dropDownCategory.title}
                </Typography>
                <Select
                  value={questionId}
                  onChange={(e) => setQuestionId(e.target.value)}
                  sx={selectSx}
                >
                  {dropDownCategory.options.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option.charAt(0).toUpperCase() + option.slice(1)}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
            ))}

          {/**
           * VISIBILITY
           */}
          <Box>
            <Typography variant="text-sm-medium" sx={formLabelSx}>
              {MODAL_DOCUMENT_STRINGS.modal.visibility}
            </Typography>
            <Select
              value={visibility}
              onChange={(e) => setVisibility(e.target.value as 'public' | 'panel')}
              sx={selectSx}
              endAdornment={
                <Box sx={chevronIconSx}>
                  <AppIcon iconName="ChevronDown" diameter="16px" color="Gray/600" />
                </Box>
              }
            >
              <MenuItem value="public">{MODAL_DOCUMENT_STRINGS.modal.public}</MenuItem>
              <MenuItem value="panel">{MODAL_DOCUMENT_STRINGS.modal.panel}</MenuItem>
            </Select>
          </Box>

          <Box sx={submitButtonContainerSx}>
            <Button
              variant="contained"
              onClick={handleSubmit}
              hierarchy="Primary"
              size="lg"
              fullWidth
              disabled={!title || !description || !questionId}
            >
              {MODAL_DOCUMENT_STRINGS.modal.addDocument}
            </Button>
          </Box>
        </Stack>
      }
    />
  );
};

const dialogSx: SxProps<Theme> = {
  zIndex: 999,
};

const formLabelSx: SxProps<Theme> = ({ backrs }) => ({
  mb: backrs.spacing['spacing-xxs'],
  color: backrs.colors['Colors/Text/text-secondary'],
});

const selectSx: SxProps<Theme> = ({ backrs }) => ({
  borderColor: backrs.colors['Colors/Border/border-primary'],
  width: 'fit-content',
  minWidth: '33%',
});

const modalDescriptionSx: SxProps<Theme> = ({ backrs }) => ({
  mb: backrs.spacing['spacing-xl'],
});

const chevronIconSx: SxProps<Theme> = ({ backrs }) => ({
  mr: backrs.spacing['spacing-md'],
});

const submitButtonContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  mt: backrs.spacing['spacing-4xl'],
});
